import "@/App.css";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, ProtectedRoute } from "@/lib/auth";
import { useAuth } from "@/lib/auth";
import Login from "@/pages/Login";
import ChangePassword from "@/pages/ChangePassword";
import LogoutPage from "@/pages/Logout";
import OfficeShell from "@/pages/OfficeShell";
import MobileShell from "@/pages/MobileShell";
import OfficeDashboard from "@/pages/OfficeDashboard";
import MobileHome from "@/pages/MobileHome";
import Stub from "@/pages/Stub";
import DemoPing from "@/pages/DemoPing";
import SyncPanel from "@/pages/SyncPanel";
import UsersList from "@/pages/admin/UsersList";
import UserVisibility from "@/pages/admin/UserVisibility";
import Lookups from "@/pages/admin/Lookups";
import Geography from "@/pages/admin/Geography";
import AuditLog from "@/pages/admin/AuditLog";
import { ProductsList, SkusList, PricingList, SchemesList } from "@/pages/admin/CatalogPages";
import { DistributorsPage, RetailersPage, BeatsPage } from "@/pages/admin/DistributionPages";
import AttendanceSettings from "@/pages/admin/AttendanceSettings";
import AttendanceScreen from "@/pages/field/AttendanceScreen";
import HolidaysPage from "@/pages/admin/HolidaysPage";
import LeavePolicyPage from "@/pages/admin/LeavePolicyPage";
import LeaveApprovalsPage from "@/pages/admin/LeaveApprovalsPage";
import LiveAttendancePage from "@/pages/admin/LiveAttendancePage";
import AttendanceReportPage from "@/pages/admin/AttendanceReportPage";
import LeaveApply from "@/pages/field/LeaveApply";
import VisitStart from "@/pages/field/VisitStart";
import VisitInProgress from "@/pages/field/VisitInProgress";
import PJPToday from "@/pages/field/PJPToday";
import GeoTagRequestsPage from "@/pages/admin/GeoTagRequestsPage";
import VisitSettingsPage from "@/pages/admin/VisitSettingsPage";
import ImportPastOrders from "@/pages/admin/ImportPastOrders";
import OutletColorCoding from "@/pages/admin/OutletColorCoding";
import TadaConfigPage from "@/pages/admin/TadaConfigPage";
import VisitReportPage from "@/pages/admin/VisitReportPage";
import OrderNew from "@/pages/field/OrderNew";
import { OrdersList, OrderDetail } from "@/pages/Orders";
import InventoryPage from "@/pages/admin/InventoryPage";
import PrimaryOrderConfig from "@/pages/admin/PrimaryOrderConfig";
import AttendanceWindows from "@/pages/admin/AttendanceWindows";
import DaRates from "@/pages/admin/DaRates";
import TadaApproval from "@/pages/admin/TadaApproval";
import LocationMap from "@/pages/admin/LocationMap";
import TravelRates from "@/pages/admin/TravelRates";
import TravelPoliciesPage from "@/pages/admin/TravelPoliciesPage";       // Phase 26B
import DistributorGroupsPage from "@/pages/admin/DistributorGroupsPage"; // Phase 26C
import PriceListsPage from "@/pages/admin/PriceListsPage";               // Phase 26C
import UomsPage from "@/pages/admin/UomsPage";                           // Phase 26D
import TadaScreen from "@/pages/field/TadaScreen";
import PJPCalendar from "@/pages/admin/PJPCalendar";           // Phase 23A
import PJPRequests from "@/pages/admin/PJPRequests";           // Phase 23A
import PJPMobile from "@/pages/field/PJPMobile";                 // Phase 23A
import OutletTransfer from "@/pages/admin/OutletTransfer";       // Phase 23B
import RetailerTransferGrid from "@/pages/admin/RetailerTransferGrid";  // Phase 26F.3 closer
import TargetBulkImport from "@/pages/admin/TargetBulkImport";  // Phase 26F.4
import InventoryDashboard from "@/pages/admin/InventoryDashboard"; // Phase 27.2
import DeviceChangeRequests from "@/pages/admin/DeviceChangeRequests"; // Phase 29.1
import GeoExceptionsPage from "@/pages/admin/GeoExceptionsPage"; // Phase 29.2
import TodayBeat from "@/pages/field/TodayBeat"; // Phase 29.2
import DistributorStockSE from "@/pages/field/DistributorStockSE";
import TodayActivity from "@/pages/field/TodayActivity"; // Itemized daily transaction log
import RetailerDashboard from "@/pages/field/RetailerDashboard"; // Phase 29.3
import SecondaryOrder from "@/pages/field/SecondaryOrder"; // Phase 29.4
import SrDashboard from "@/pages/field/SrDashboard"; // Phase 29.6.1
import MyDaySummary from "@/pages/field/MyDaySummary"; // Phase 29.6.2
import MoreMenu from "@/pages/field/MoreMenu";
import ViewModeChooser from "@/pages/ViewModeChooser";
import AttendanceCalendar from "@/pages/AttendanceCalendar";
import AttendancePolicy from "@/pages/admin/AttendancePolicy";
import DistributorTargets from "@/pages/admin/DistributorTargets";
import MyKgTargets from "@/pages/MyKgTargets";
import TeamDaySummaries from "@/pages/manager/TeamDaySummaries"; // Phase 29.6.2
import RetailerListDownload from "@/pages/manager/RetailerListDownload"; // ASM More menu
import ReportsHub from "@/pages/manager/ReportsHub"; // ASM More menu
import TeamClaimsSummary from "@/pages/manager/TeamClaimsSummary"; // ASM More menu
import ExceptionsPanel from "@/pages/management/ExceptionsPanel"; // Phase 29.6.3
import ExceptionDetail from "@/pages/management/ExceptionDetail"; // Phase 29.6.3
import ExceptionRulesAdmin from "@/pages/management/ExceptionRulesAdmin"; // Phase 29.6.3 fix
import FocusSkusAdmin from "@/pages/admin/FocusSkusAdmin"; // Phase 29.4
import OutletConfig from "@/pages/admin/OutletConfig";           // Phase 23B
import OutletDetail from "@/pages/admin/OutletDetail";           // Phase 23B
import OutletDensityMap from "@/pages/admin/OutletDensityMap";   // Phase 23C
import LastLoginReport from "@/pages/admin/reports/LastLoginReport";       // Phase 24A
import SeDailySummary from "@/pages/field/SeDailySummary";                 // Phase 24D
import MobileHealthReport from "@/pages/admin/reports/MobileHealthReport"; // Phase 24A
import InventoryReport from "@/pages/admin/reports/InventoryReport";       // Phase 24A
import OrderReport from "@/pages/admin/reports/OrderReport";               // Phase 24B
import SchemeReport from "@/pages/admin/reports/SchemeReport";
import PrimarySchemeReport from "@/pages/admin/reports/PrimarySchemeReport";
import VisitReport24B from "@/pages/admin/reports/VisitReport";            // Phase 24B
import WorkingReport from "@/pages/admin/reports/WorkingReport";           // Phase 24B
import NewCallReport from "@/pages/admin/reports/NewCallReport";           // Phase 24B
import SalesReturnReport from "@/pages/admin/reports/SalesReturnReport";   // Phase 24B
import DaySummaryReport from "@/pages/admin/reports/DaySummaryReport";     // Phase 26D.3
import AdminOrderGrid from "@/pages/admin/AdminOrderGrid";                   // Phase 26E.1
import AdminClaimGrid from "@/pages/admin/AdminClaimGrid";                   // Phase 26E.2
import { ClaimReport, PrimaryOrderReport, PrimarySalesReturnReport, SalesAndReturnsReport, UserKmReport, DeviceAccessReport, DistributorQueryReport, DistributorQueryConversationReport, BeatReport, InactiveOutletsReport } from "@/pages/admin/reports/Phase24CReports";  // Phase 24C
import SystemSetup from "@/pages/admin/SystemSetup";                              // Phase 25A
import AddOutlet from "@/pages/field/AddOutlet";                 // Phase 23B.5
import OutletsList from "@/pages/field/OutletsList";             // Phase 23B.5
import AdminFormsList from "@/pages/admin/AdminFormsList";
import FormBuilder from "@/pages/admin/FormBuilder";
import FormsList from "@/pages/field/FormsList";
import FormFill from "@/pages/field/FormFill";
import CallForms from "@/pages/field/CallForms";
import MyActivitySubmissions from "@/pages/field/MyActivitySubmissions";
import ActivitySubmissionsList from "@/pages/admin/ActivitySubmissionsList";
import ActivityReportPage from "@/pages/admin/ActivityReportPage";
import DisplayCompliancePage from "@/pages/admin/DisplayCompliancePage";
import MoneyHub from "@/pages/field/money/MoneyHub";
import PaymentNew, { PaymentsList } from "@/pages/field/money/Payments";
import ClaimNew, { ClaimsList } from "@/pages/field/money/Claims";
import SaleReturnNew, { SaleReturnsList } from "@/pages/field/money/SaleReturns";
import ExpenseNew, { ExpensesList } from "@/pages/field/money/Expenses";
import MoneyInboxPage from "@/pages/admin/MoneyInboxPage";
import MoneyReports from "@/pages/admin/MoneyReports";
import ApprovalConfigsPage from "@/pages/admin/ApprovalConfigsPage";
import ApprovalsInbox from "@/pages/admin/ApprovalsInbox";
import TargetsPage from "@/pages/admin/TargetsPage";
import TargetAchievementReport from "@/pages/admin/TargetAchievementReport";
import MyTargets from "@/pages/field/MyTargets";
// Phase 15 — Distributor Portal
import DistributorShell from "@/pages/DistributorShell";
import PortalHome from "@/pages/portal/Home";
import PortalOrders from "@/pages/portal/Orders";
import PortalStock from "@/pages/portal/Stock";
import PortalBeat from "@/pages/portal/Beat";
import PortalDaySummary from "@/pages/portal/DaySummary";
import PortalSalesReport from "@/pages/portal/SalesReport";
import PrimaryOrderField from "@/pages/field/PrimaryOrder";
import PrimarySalesReturn from "@/pages/portal/PrimarySalesReturn";
import PrimarySaleReturnApproval from "@/pages/admin/PrimarySaleReturnApproval";
import QueryTracker from "@/components/queries/QueryTracker";
import QueryReasons from "@/pages/admin/QueryReasons";
// Phase 16 — Dashboard / SMTP / Notifications / Schedules
import ManagementDashboard from "@/pages/admin/ManagementDashboard";
import EmailSettingsPage from "@/pages/admin/EmailSettingsPage";
import SchedulesPage from "@/pages/admin/SchedulesPage";
import NotificationPrefsPage from "@/pages/NotificationPrefsPage";
// Broadcast messages — admin sends to many users; everyone reads in /messages
import Broadcasts from "@/pages/admin/Broadcasts";
import Messages from "@/pages/Messages";
// Phase 17 — AI
import InsightsPage from "@/pages/insights/InsightsPage";
import AskPage from "@/pages/insights/AskPage";
import { startAutoSync } from "@/offline/sync";
import { refreshAll } from "@/offline/masters";
import { useEffect } from "react";

function RoleAwareShell({ children }) {
  const { user, isField, needsViewChoice } = useAuth();
  if (user?.role_code === "DISTRIBUTOR") {
    return <DistributorShell>{children}</DistributorShell>;
  }
  // ASM / ZSM choose Field app or Browser once per device.
  if (needsViewChoice) return <ViewModeChooser />;
  return isField ? <MobileShell>{children}</MobileShell> : <OfficeShell>{children}</OfficeShell>;
}

function RoleAwareHome() {
  const { user, isField } = useAuth();
  if (user?.role_code === "DISTRIBUTOR") {
    return <Navigate to="/portal/home" replace />;
  }
  // Coordinator has no dashboard access — their home is the query inbox.
  if (user?.role_code === "SALES_COORDINATOR") {
    return <Navigate to="/admin/queries" replace />;
  }
  return isField ? <MobileHome /> : <OfficeDashboard />;
}

// Route guards for Phase 15 Distributor Portal
function PortalRoute({ children }) {
  const { user } = useAuth();
  if (user?.role_code !== "DISTRIBUTOR") return <Navigate to="/" replace />;
  return children;
}

function NonPortalRoute({ children }) {
  // Distributors hitting non-portal routes → redirect to portal.
  const { user } = useAuth();
  if (user?.role_code === "DISTRIBUTOR") return <Navigate to="/portal/home" replace />;
  return children;
}

function OfflineBootstrap() {
  const { user } = useAuth();
  useEffect(() => {
    const stop = startAutoSync();
    return () => { if (typeof stop === "function") stop(); };
  }, []);
  useEffect(() => {
    if (user) refreshAll({ force: false }).catch(() => {});
  }, [user?.id]);
  return null;
}

// Radix (Select/Dialog) sets `pointer-events: none` on <body> while open and
// restores it on close — but navigating away mid-interaction (e.g. tapping
// "Back" while a beat/category dropdown is still open) can unmount the
// component before that cleanup runs, leaving the whole app permanently
// unclickable with no visible error. Clearing it on every route change is
// the standard workaround — a no-op when nothing was stuck, so it's safe to
// run unconditionally.
function RadixPointerEventsGuard() {
  const location = useLocation();
  useEffect(() => {
    if (document.body.style.pointerEvents === "none") {
      document.body.style.pointerEvents = "";
    }
  }, [location.key]);
  return null;
}

function AdminRoute({ children, permissions = [], roles = [], allowField = false }) {
  const { user, hasAnyPermission } = useAuth();
  const location = useLocation();
  // Shared client-side guard for /admin/* and /manager/* — prevents empty
  // shell flash for field roles (SE) and distributors on direct URL
  // navigation. `allowField=true` is an explicit opt-in escape hatch.
  const isAdminPath = location.pathname.startsWith("/admin/") || location.pathname.startsWith("/manager/");
  if (isAdminPath && !allowField) {
    if (user?.role_code === "DISTRIBUTOR") {
      return <Navigate to="/portal/home" replace />;
    }
    if (user?.role_code === "SE") {
      return <Navigate to="/" replace />;
    }
  }
  const roleOk = roles.length === 0 || roles.includes(user?.role_code);
  const permOk = permissions.length === 0 || hasAnyPermission(permissions);
  if (!roleOk || !permOk) return <Navigate to="/" replace />;
  return children;
}

const office = (node) => (
  <ProtectedRoute>
    <RoleAwareShell>{node}</RoleAwareShell>
  </ProtectedRoute>
);

// Primary order (distributor -> company) is a desk-based workflow, not a
// beat/field action — no role should need to have punched attendance to
// add/edit/accept/reject one (matches the backend exemption already added
// to routes_phase29_1.py's EXEMPT_PATH_PREFIXES). Every other office()
// route keeps the default attendance-first gate.
const officeNoAttendance = (node) => (
  <ProtectedRoute requireAttendance={false}>
    <RoleAwareShell>{node}</RoleAwareShell>
  </ProtectedRoute>
);

// Route / Beat / TA-DA are the one exception the other direction: ASM and
// ZSM are otherwise NOT attendance-gated (see auth.jsx), but these three
// specific screens still require them to have punched in today, same as SE.
const officeRouteBeatTada = (node) => (
  <ProtectedRoute strictRoles={["SE", "ASM", "ZSM"]}>
    <RoleAwareShell>{node}</RoleAwareShell>
  </ProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <OfflineBootstrap />
      <BrowserRouter>
        <RadixPointerEventsGuard />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute requireChangedPassword={false} requireAttendance={false}>
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route path="/logout" element={<LogoutPage />} />

          <Route path="/" element={office(<RoleAwareHome />)} />

          {/* Offline demo + sync panel */}
          <Route path="/sync" element={office(<SyncPanel />)} />
          <Route path="/demo/ping" element={office(<DemoPing />)} />

          {/* Admin */}
          <Route
            path="/admin/users"
            element={office(<AdminRoute permissions={["users.manage"]}><UsersList /></AdminRoute>)}
          />
          <Route
            path="/admin/users/:userId/visibility"
            element={office(<AdminRoute permissions={["users.manage", "visibility.override"]}><UserVisibility /></AdminRoute>)}
          />
          <Route
            path="/admin/lookups"
            element={office(<AdminRoute permissions={["lookups.manage"]}><Lookups /></AdminRoute>)}
          />
          <Route
            path="/admin/geography"
            element={office(<AdminRoute permissions={["masters.manage"]}><Geography /></AdminRoute>)}
          />
          <Route
            path="/admin/audit"
            element={office(<AdminRoute roles={["SUPER_ADMIN", "MIS", "MANAGEMENT"]}><AuditLog /></AdminRoute>)}
          />

          {/* Phase 25A — System Setup (in-app migration runner) */}
          <Route
            path="/admin/system-setup"
            element={office(<AdminRoute roles={["SUPER_ADMIN"]}><SystemSetup /></AdminRoute>)}
          />

          {/* Phase 6 — Catalog */}
          <Route path="/admin/products" element={office(<AdminRoute permissions={["masters.manage"]}><ProductsList /></AdminRoute>)} />
          <Route path="/admin/skus" element={office(<AdminRoute permissions={["masters.manage"]}><SkusList /></AdminRoute>)} />
          <Route path="/admin/pricing" element={office(<AdminRoute permissions={["masters.manage"]}><PricingList /></AdminRoute>)} />
          <Route path="/admin/schemes" element={office(<AdminRoute permissions={["masters.manage"]}><SchemesList /></AdminRoute>)} />

          {/* Phase 7 — Distribution Network */}
          <Route path="/admin/distributors" element={office(<AdminRoute permissions={["masters.manage"]}><DistributorsPage /></AdminRoute>)} />
          <Route path="/admin/retailers" element={office(<AdminRoute permissions={["masters.manage"]}><RetailersPage /></AdminRoute>)} />
          <Route path="/admin/beats" element={office(<AdminRoute permissions={["masters.manage"]}><BeatsPage /></AdminRoute>)} />

          {/* Phase 21B — Distributor Inventory Engine */}
          <Route path="/admin/inventory" element={office(<AdminRoute permissions={["masters.manage"]}><InventoryPage /></AdminRoute>)} />

          {/* Phase 21C — Primary Order Config */}
          <Route path="/admin/primary-order-config" element={office(<AdminRoute permissions={["masters.manage"]}><PrimaryOrderConfig /></AdminRoute>)} />

          {/* Phase 22A — Attendance Time Windows */}
          <Route path="/admin/attendance-windows" element={office(<AdminRoute permissions={["masters.manage"]}><AttendanceWindows /></AdminRoute>)} />

          {/* Phase 22B — TA/DA rate masters + field claim flow */}
          <Route path="/admin/da-rates"     element={office(<AdminRoute permissions={["masters.manage"]}><DaRates /></AdminRoute>)} />
          <Route path="/admin/travel-rates" element={office(<AdminRoute permissions={["masters.manage"]}><TravelRates /></AdminRoute>)} />
          <Route path="/admin/travel-policies" element={office(<AdminRoute permissions={["masters.manage"]}><TravelPoliciesPage /></AdminRoute>)} />
          <Route path="/admin/distributor-groups" element={office(<AdminRoute permissions={["masters.manage"]}><DistributorGroupsPage /></AdminRoute>)} />
          <Route path="/admin/price-lists" element={office(<AdminRoute permissions={["masters.manage"]}><PriceListsPage /></AdminRoute>)} />
          <Route path="/admin/uoms" element={office(<AdminRoute permissions={["masters.manage"]}><UomsPage /></AdminRoute>)} />
          <Route path="/tada" element={officeRouteBeatTada(<TadaScreen />)} />

          {/* Phase 22C — Approval + Location Map */}
          <Route path="/admin/tada-approval" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><TadaApproval /></AdminRoute>)} />
          <Route path="/admin/user-map"       element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><LocationMap /></AdminRoute>)} />

          {/* Phase 23A — PJP Upgrade */}
          <Route path="/admin/pjp"          element={office(<AdminRoute permissions={["pjp.manage"]}><PJPCalendar /></AdminRoute>)} />
          <Route path="/admin/pjp-requests" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><PJPRequests /></AdminRoute>)} />
          <Route path="/pjp"                element={officeRouteBeatTada(<PJPMobile />)} />

          {/* Phase 23B — Outlet upgrades */}
          <Route path="/admin/outlet-transfer" element={office(<AdminRoute permissions={["masters.manage"]}><OutletTransfer /></AdminRoute>)} />
          <Route path="/admin/retailer-transfer" element={office(<AdminRoute permissions={["masters.manage"]}><RetailerTransferGrid /></AdminRoute>)} />
          <Route path="/admin/target-bulk-import" element={office(<AdminRoute permissions={["masters.manage"]}><TargetBulkImport /></AdminRoute>)} />
          <Route path="/admin/outlet-config"    element={office(<AdminRoute permissions={["masters.manage"]}><OutletConfig /></AdminRoute>)} />
          <Route path="/admin/outlets/:retailerId" element={office(<AdminRoute permissions={["masters.view","masters.manage"]}><OutletDetail /></AdminRoute>)} />

          {/* Phase 23C — Outlet Density Map */}
          <Route path="/admin/outlet-density-map" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><OutletDensityMap /></AdminRoute>)} />

          {/* Phase 24A — Report suite (Last Login / Mobile Health / Inventory) */}
          <Route path="/reports/last-login"    element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><LastLoginReport /></AdminRoute>)} />
          <Route path="/reports/mobile-health" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><MobileHealthReport /></AdminRoute>)} />
          <Route path="/reports/inventory"     element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","DISTRIBUTOR"]}><InventoryReport /></AdminRoute>)} />

          {/* Phase 27.2 — Real-Time Inventory + Variance Dashboard */}
          <Route path="/admin/inventory-dashboard" element={office(<AdminRoute permissions={["reports.view"]}><InventoryDashboard /></AdminRoute>)} />

          {/* Phase 29.1 — Admin: Device change requests review */}
          <Route path="/admin/device-change-requests" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT"]}><DeviceChangeRequests /></AdminRoute>)} />

          {/* Phase 29.2 — Manager Start-Call geo-exception approvals + SE Today's Beat */}
          <Route path="/admin/geo-exceptions" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","SALES_HEAD","ZSM","RSM","ASM"]}><GeoExceptionsPage /></AdminRoute>)} />
          <Route path="/today-beat" element={officeRouteBeatTada(<TodayBeat />)} />
          <Route path="/primary-order" element={officeNoAttendance(<NonPortalRoute><PrimaryOrderField /></NonPortalRoute>)} />
          <Route path="/distributor-stock" element={office(<DistributorStockSE />)} />
          <Route path="/today-activity" element={office(<TodayActivity />)} />

          {/* Phase 29.3 — Retailer Dashboard (auto-open after Start Call) */}
          <Route path="/field/retailer/:retailer_id/dashboard" element={office(<RetailerDashboard />)} />
          <Route path="/field/sr-dashboard" element={office(<SrDashboard />)} />
          <Route path="/field/my-day-summary" element={office(<MyDaySummary />)} />
          <Route path="/manager/team-day-summaries" element={office(<TeamDaySummaries />)} />
          <Route path="/manager/retailer-list" element={office(<RetailerListDownload />)} />
          <Route path="/manager/reports-hub" element={office(<ReportsHub />)} />
          <Route path="/manager/team-claims" element={office(<TeamClaimsSummary />)} />
          <Route path="/management/exceptions" element={office(<ExceptionsPanel />)} />
          <Route path="/management/exceptions/:id" element={office(<ExceptionDetail />)} />
          <Route path="/admin/exception-rules" element={office(<ExceptionRulesAdmin />)} />

          {/* Phase 29.4 — Secondary Order + Focus-SKU admin */}
          <Route path="/field/retailer/:retailer_id/secondary-order" element={office(<SecondaryOrder />)} />
          <Route path="/admin/focus-skus" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT"]}><FocusSkusAdmin /></AdminRoute>)} />
          {/* Phase 24B — Transaction reports (Order/Visit/Working/NewCall/SalesReturn) */}
          <Route path="/reports/order"         element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","SE","DISTRIBUTOR"]}><OrderReport /></AdminRoute>)} />
          <Route path="/reports/scheme"        element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","SE","DISTRIBUTOR"]}><SchemeReport /></AdminRoute>)} />
          <Route path="/reports/primary-schemes" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","DISTRIBUTOR"]}><PrimarySchemeReport /></AdminRoute>)} />
          <Route path="/reports/visit"         element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","SE"]}><VisitReport24B /></AdminRoute>)} />
          <Route path="/reports/working"       element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","SE"]}><WorkingReport /></AdminRoute>)} />
          <Route path="/reports/new-call"      element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","SE"]}><NewCallReport /></AdminRoute>)} />
          <Route path="/reports/sales-return"  element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","SE","DISTRIBUTOR"]}><SalesReturnReport /></AdminRoute>)} />

          {/* Phase 26D.3 — Day Summary (Bizom-reference SKU breakdown, KG-primary) */}
          <Route path="/reports/day-summary"   element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","SE"]}><DaySummaryReport /></AdminRoute>)} />

          {/* Phase 26E.1 — Admin Order Grid (ERP-style bulk actions) */}
          <Route path="/admin/orders"          element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><AdminOrderGrid /></AdminRoute>)} />

          {/* Phase 26E.2 — Admin Claim (TA/DA) Grid */}
          <Route path="/admin/claims/grid"     element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><AdminClaimGrid /></AdminRoute>)} />

          {/* Phase 24D — SE Day-End Summary (mobile) */}
          <Route path="/summary" element={office(<SeDailySummary />)} />

          {/* Phase 24C — Claim / Primary / Combined */}
          <Route path="/reports/claims"                element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","SE"]}><ClaimReport /></AdminRoute>)} />
          <Route path="/reports/primary-orders"        element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","DISTRIBUTOR"]}><PrimaryOrderReport /></AdminRoute>)} />
          <Route path="/reports/device-access" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT"]}><DeviceAccessReport /></AdminRoute>)} />
          <Route path="/reports/user-km" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM"]}><UserKmReport /></AdminRoute>)} />
          <Route path="/reports/primary-sales-returns" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","DISTRIBUTOR"]}><PrimarySalesReturnReport /></AdminRoute>)} />
          <Route path="/reports/sales-and-returns"     element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","ZSM","RSM","ASM","DISTRIBUTOR"]}><SalesAndReturnsReport /></AdminRoute>)} />

          {/* Phase 23B.5 — SE mobile outlets */}
          <Route path="/outlets"     element={office(<OutletsList />)} />
          <Route path="/outlets/new" element={office(<AddOutlet />)} />

          {/* Phase 8 — Attendance */}
          <Route path="/admin/attendance-policy" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT"]}><AttendancePolicy /></AdminRoute>)} />
          <Route path="/admin/attendance-settings" element={office(<AdminRoute permissions={["masters.manage"]}><AttendanceSettings /></AdminRoute>)} />
          <Route path="/attendance" element={office(<AttendanceScreen />)} />
          {/* Own-month attendance calendar — viewable even before punching in. */}
          <Route path="/my-targets" element={officeNoAttendance(<NonPortalRoute><MyKgTargets /></NonPortalRoute>)} />
          <Route path="/sales-report" element={office(<AdminRoute roles={["SE","ASM","ZSM","RSM","SALES_HEAD","SUPER_ADMIN","MANAGEMENT","MIS"]}><PortalSalesReport /></AdminRoute>)} />
          <Route path="/portal/targets" element={office(<PortalRoute><MyKgTargets /></PortalRoute>)} />
          <Route path="/admin/distributor-targets" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","SALES_COORDINATOR"]}><DistributorTargets /></AdminRoute>)} />
          <Route path="/attendance-calendar" element={officeNoAttendance(<NonPortalRoute><AttendanceCalendar /></NonPortalRoute>)} />

          {/* Phase 9 — Leave, Holidays, Reports, Manager Live View */}
          <Route path="/admin/holidays" element={office(<AdminRoute permissions={["masters.manage"]}><HolidaysPage /></AdminRoute>)} />
          <Route path="/admin/leave-policy" element={office(<AdminRoute permissions={["masters.manage"]}><LeavePolicyPage /></AdminRoute>)} />
          <Route path="/leave-approvals" element={office(<AdminRoute permissions={["leave.approve"]}><LeaveApprovalsPage /></AdminRoute>)} />
          <Route path="/live-attendance" element={office(<AdminRoute permissions={["reports.attendance"]}><LiveAttendancePage /></AdminRoute>)} />
          <Route path="/reports/attendance" element={office(<AdminRoute permissions={["reports.attendance"]} roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","RSM","SALES_COORDINATOR"]}><AttendanceReportPage /></AdminRoute>)} />
          <Route path="/leave" element={office(<LeaveApply />)} />

          {/* Phase 10 — Visits / PJP / Geo-tag / Reports */}
          <Route path="/visits/start/:retailer_id" element={office(<VisitStart />)} />
          <Route path="/visits/:visit_id/in-progress" element={office(<VisitInProgress />)} />
          <Route path="/pjp/today" element={office(<PJPToday />)} />
          <Route path="/manager/geo-tag-requests" element={office(<AdminRoute permissions={["geo_tag.approve"]}><GeoTagRequestsPage /></AdminRoute>)} />
          <Route path="/admin/visit-settings" element={office(<AdminRoute permissions={["masters.manage"]}><VisitSettingsPage /></AdminRoute>)} />
          <Route path="/admin/import-past-orders" element={office(<AdminRoute roles={["SUPER_ADMIN"]}><ImportPastOrders /></AdminRoute>)} />
          <Route path="/admin/outlet-colors" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","SALES_COORDINATOR"]}><OutletColorCoding /></AdminRoute>)} />
          <Route path="/admin/tada-config" element={office(<AdminRoute permissions={["masters.manage"]}><TadaConfigPage /></AdminRoute>)} />
          <Route path="/reports/beat-coverage" element={office(<AdminRoute permissions={["reports.visits","reports.view"]}><VisitReportPage kind="beat-coverage" /></AdminRoute>)} />
          <Route path="/reports/missed-visits" element={office(<AdminRoute permissions={["reports.visits","reports.view"]}><VisitReportPage kind="missed-visits" /></AdminRoute>)} />
          <Route path="/reports/daily-visit-summary" element={office(<AdminRoute permissions={["reports.visits","reports.view"]}><VisitReportPage kind="daily-visit-summary" /></AdminRoute>)} />

          {/* Phase 11 — Orders */}
          <Route path="/orders" element={office(<OrdersList />)} />
          <Route path="/orders/new" element={office(<OrderNew />)} />
          <Route path="/orders/:order_id" element={office(<OrderDetail />)} />

          {/* Phase 12 — Activity Forms */}
          <Route path="/admin/forms" element={office(<AdminRoute permissions={["forms.manage"]}><AdminFormsList /></AdminRoute>)} />
          <Route path="/admin/forms/:form_id/builder" element={office(<AdminRoute permissions={["forms.manage"]}><FormBuilder /></AdminRoute>)} />
          <Route path="/forms" element={office(<FormsList />)} />
          <Route path="/field/my-activity-submissions" element={office(<MyActivitySubmissions />)} />
          <Route path="/forms/:form_id/fill" element={office(<FormFill />)} />
          <Route path="/field/call-forms/:visit_id" element={office(<CallForms />)} />
          <Route path="/manager/activity-submissions" element={office(<AdminRoute permissions={["activities.verify"]}><ActivitySubmissionsList /></AdminRoute>)} />
          <Route path="/reports/activity-submissions" element={office(<AdminRoute permissions={["reports.activity","reports.activity.own","reports.view"]}><ActivityReportPage /></AdminRoute>)} />
          <Route path="/reports/display-compliance" element={office(<AdminRoute permissions={["reports.activity","reports.view"]}><DisplayCompliancePage /></AdminRoute>)} />

          {/* Phase 13 — Money Modules.
              Hotfix — restricted to SUPER_ADMIN for now (user request: pull
              Money from every other role's app; re-add for others if/when
              they ask). The bottom-nav tab is already hidden in
              MobileShell.jsx — this route guard covers direct/bookmarked
              URLs too. */}
          <Route path="/money"                     element={office(<AdminRoute roles={["SUPER_ADMIN"]}><MoneyHub /></AdminRoute>)} />
          <Route path="/money/payments"            element={office(<AdminRoute roles={["SUPER_ADMIN"]}><PaymentsList /></AdminRoute>)} />
          <Route path="/money/payments/new"        element={office(<AdminRoute roles={["SUPER_ADMIN"]}><PaymentNew /></AdminRoute>)} />
          <Route path="/money/claims"              element={office(<AdminRoute roles={["SUPER_ADMIN"]}><ClaimsList /></AdminRoute>)} />
          <Route path="/money/claims/new"          element={office(<AdminRoute roles={["SUPER_ADMIN"]}><ClaimNew /></AdminRoute>)} />
          <Route path="/money/sale-returns"        element={office(<AdminRoute roles={["SUPER_ADMIN"]}><SaleReturnsList /></AdminRoute>)} />
          <Route path="/money/sale-returns/new"    element={office(<AdminRoute roles={["SUPER_ADMIN"]}><SaleReturnNew /></AdminRoute>)} />
          <Route path="/money/expenses"            element={office(<AdminRoute roles={["SUPER_ADMIN"]}><ExpensesList /></AdminRoute>)} />
          <Route path="/money/expenses/new"        element={office(<AdminRoute roles={["SUPER_ADMIN"]}><ExpenseNew /></AdminRoute>)} />
          <Route path="/manager/money-inbox"       element={office(<AdminRoute permissions={["claims.approve","payments.verify","reports.view"]}><MoneyInboxPage /></AdminRoute>)} />
          <Route path="/reports/money"             element={office(<AdminRoute permissions={["reports.payments","reports.claims","reports.expenses","reports.view"]} roles={["SUPER_ADMIN","MANAGEMENT","MIS","ACCOUNTS","SALES_HEAD","RSM","SALES_COORDINATOR"]}><MoneyReports /></AdminRoute>)} />

          {/* Phase 14 — Approval engine + Targets */}
          <Route path="/admin/approval-configs"    element={office(<AdminRoute permissions={["approvals.manage"]}><ApprovalConfigsPage /></AdminRoute>)} />
          <Route path="/approvals/inbox"           element={office(<ApprovalsInbox />)} />
          <Route path="/targets"                   element={office(<AdminRoute permissions={["targets.set","targets.view"]}><TargetsPage /></AdminRoute>)} />
          <Route path="/reports/target-achievement" element={office(<AdminRoute permissions={["reports.targets","reports.targets.own","reports.view","targets.view"]}><TargetAchievementReport /></AdminRoute>)} />
          <Route path="/targets/mine"              element={office(<MyTargets />)} />

          {/* Phase 15 — Distributor Portal (DISTRIBUTOR role only) */}
          <Route path="/portal/home"            element={office(<PortalRoute><PortalHome /></PortalRoute>)} />
          <Route path="/portal/orders"          element={office(<PortalRoute><PortalOrders /></PortalRoute>)} />
          <Route path="/portal/stock"           element={office(<PortalRoute><PortalStock /></PortalRoute>)} />
          <Route path="/portal/beat"            element={office(<PortalRoute><PortalBeat /></PortalRoute>)} />
          <Route path="/portal/primary-orders"  element={office(<PortalRoute><PrimaryOrderField /></PortalRoute>)} />
          <Route path="/portal/primary-sales-return" element={office(<PortalRoute><PrimarySalesReturn /></PortalRoute>)} />
          <Route path="/portal/day-summary"     element={office(<PortalRoute><PortalDaySummary /></PortalRoute>)} />
          <Route path="/portal/sales-report"    element={office(<PortalRoute><PortalSalesReport /></PortalRoute>)} />
          <Route path="/portal/queries"         element={office(<PortalRoute><QueryTracker mode="portal" /></PortalRoute>)} />

          {/* Distributor Query Tracker — handlers/MANAGEMENT inbox, reason
              master, report, and the SE/ASM/ZSM view (no attendance gate:
              SE must be able to raise a query without punching in). */}
          <Route path="/admin/queries"         element={office(<AdminRoute roles={["SUPER_ADMIN","SALES_COORDINATOR","MANAGEMENT"]}><QueryTracker mode="admin" /></AdminRoute>)} />
          <Route path="/admin/query-reasons"   element={office(<AdminRoute roles={["SUPER_ADMIN","SALES_COORDINATOR"]}><QueryReasons /></AdminRoute>)} />
          <Route path="/queries"               element={officeNoAttendance(<NonPortalRoute><QueryTracker mode="field" /></NonPortalRoute>)} />
          <Route path="/reports/distributor-query-conversations" element={office(<AdminRoute roles={["SUPER_ADMIN","SALES_COORDINATOR","MANAGEMENT"]}><DistributorQueryConversationReport /></AdminRoute>)} />
          <Route path="/reports/inactive-outlets" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","SALES_HEAD","RSM"]}><InactiveOutletsReport /></AdminRoute>)} />
          <Route path="/reports/beat-report" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","MIS","SALES_HEAD","RSM"]}><BeatReport /></AdminRoute>)} />
          <Route path="/reports/distributor-queries" element={office(<AdminRoute roles={["SUPER_ADMIN","SALES_COORDINATOR","MANAGEMENT"]}><DistributorQueryReport /></AdminRoute>)} />

          {/* Primary Sale Return approval — ASM (stage 1) then ZSM (stage
              2, final). Same admin-override roles as other 2-stage
              approvals in this app. */}
          <Route path="/admin/primary-sale-return-approval"
                 element={office(<AdminRoute roles={["ASM","ZSM","SUPER_ADMIN","MANAGEMENT"]}><PrimarySaleReturnApproval /></AdminRoute>)} />

          {/* Phase 16 — Reports Suite + Dashboard + SMTP */}
          <Route path="/dashboard"             element={office(<AdminRoute permissions={["dashboard.view","reports.view","users.manage"]}><ManagementDashboard /></AdminRoute>)} />
          <Route path="/admin/email-settings"  element={office(<AdminRoute permissions={["email.manage"]}><EmailSettingsPage /></AdminRoute>)} />
          <Route path="/admin/schedules"       element={office(<SchedulesPage />)} />
          <Route path="/settings/notifications" element={office(<NotificationPrefsPage />)} />

          {/* Broadcast messages. Sending is limited to the three sender
              roles; reading is open to EVERY logged-in role (distributor
              portal and coordinator included) and not attendance-gated, so
              an SE can read an announcement before punching in. */}
          <Route path="/admin/broadcasts" element={office(<AdminRoute roles={["SUPER_ADMIN","MANAGEMENT","SALES_COORDINATOR"]}><Broadcasts /></AdminRoute>)} />
          <Route path="/messages"     element={officeNoAttendance(<Messages />)} />
          <Route path="/messages/:id" element={officeNoAttendance(<Messages />)} />

          {/* Phase 17 — AI Layer */}
          <Route path="/insights"      element={office(<AdminRoute permissions={["ai.view","dashboard.view","reports.view"]}><InsightsPage /></AdminRoute>)} />
          <Route path="/insights/ask"  element={office(<AdminRoute permissions={["ai.ask","dashboard.view","users.manage"]}><AskPage /></AdminRoute>)} />
          {/* Stubs — future business modules */}
          <Route path="/approvals" element={office(<Stub title="Approvals" subtitle="Order / claim / expense approval inbox — Phase 4." testid="stub-approvals" />)} />
          <Route path="/reports" element={office(<Stub title="Reports" subtitle="Coverage, productivity, target vs achievement — Phase 4." testid="stub-reports" />)} />
          <Route path="/visits" element={office(<Stub title="Visits" subtitle="Retailer visit workflow — Phase 4." testid="stub-visits" />)} />
          <Route path="/orders" element={office(<Stub title="Orders" subtitle="Order booking — Phase 4." testid="stub-orders" />)} />
          {/* /more itself only navigates elsewhere — each destination still
              has its own attendance gate via office()/officeNoAttendance().
              An unpunched SE/ASM must still be able to reach this menu to
              tap "Primary Order" (exempted above), so this route can't stay
              behind the default attendance gate either. */}
          <Route path="/more" element={officeNoAttendance(<MoreMenu />)} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-center" />
    </AuthProvider>
  );
}

export default App;
